/**
 * Stable error type for chat-attachment text extraction.
 *
 * Kept in its own module so the extractor stays import-cycle-free
 * (other UI modules can `instanceof` these without pulling in the
 * streaming/fetch plumbing).
 */

export type SessionFileTextExtractionErrorCode =
  | "aborted"
  | "network"
  | "not_found"
  | "unreadable"
  | "too_large"
  | "skipped";

export interface SessionFileTextExtractionError extends Error {
  readonly code: SessionFileTextExtractionErrorCode;
  readonly cause?: unknown;
}

export function sessionFileTextExtractionError(
  code: SessionFileTextExtractionErrorCode,
  message: string,
  cause?: unknown,
): SessionFileTextExtractionError {
  const err = new Error(message) as SessionFileTextExtractionError;
  err.name = "SessionFileTextExtractionError";
  (err as { code: SessionFileTextExtractionErrorCode }).code = code;
  if (cause !== undefined) (err as { cause?: unknown }).cause = cause;
  return err;
}

export function isSessionFileTextExtractionError(value: unknown): value is SessionFileTextExtractionError {
  return Boolean(
    value &&
      typeof value === "object" &&
      "code" in value &&
      typeof (value as { code?: unknown }).code === "string" &&
      (value as { name?: unknown }).name === "SessionFileTextExtractionError",
  );
}
